import { useMemo, useRef, useState } from 'react';
import {
  furnitureCatalog, findFurniture, FURNITURE_CATEGORIES, sheetCatalog, findSheet, stampId, SHEET_TILE_SIZE,
  furnitureFamilyId, orientations, FURNITURE_VIEW_LABELS,
} from '@campus/core';
import type { FurnitureCatalogEntry, SheetStamp } from '@campus/core';
import { Button, Icon, InfoTip, Input, Switch, Tabs, cx } from '../ds/index.js';

/**
 * Palette mobilier de l'éditeur (Mobilier), trois onglets :
 * - Objets (façon WorkAdventure) : la sélection de l'outil de curation, par
 *   catégorie, chaque objet affiché avec toutes ses cases ; un objet issu
 *   d'une planche peut n'être posé qu'en partie (glisser sur ses cases).
 * - Planches (façon Tiled) : la planche LimeZu entière, on glisse pour
 *   sélectionner n'importe quel rectangle de cases (tampon).
 * - Animés : les objets animés `lz_anim_*` (cascades, fontaines, écrans…),
 *   sortis d'« Objets » pour la lisibilité.
 * Un tampon est un ID `lz_{planche}_r{x}_{y}_{l}x{h}[_fx]` (voir sheetCatalog).
 */

const BORDER = 'var(--color-border-default)';
const DEFAULT_PANEL_WIDTH = 400;
const T = SHEET_TILE_SIZE;
/** Fond des aperçus : gris neutre, ni parquet ni gazon, pour juger l'objet seul. */
const PREVIEW_BG = '#3a3f4b';
const GRID_LINE = 'rgba(255,255,255,0.14)';

// Palette = entrées sélectionnées uniquement (voir tools/assets-cli
// furniture-curation.json) — les entrées `hidden` ne servent qu'à afficher le
// mobilier déjà posé.
const paletteEntries = furnitureCatalog.filter((e) => !e.hidden);
// Un objet décliné en vues (face, dos, profils : `…-face`, `…-dos`…) n'a
// qu'une vignette, sa première vue ; on passe d'une vue à l'autre dans le
// détail ou avec R. La recherche porte sur les titres de toutes ses vues.
const paletteFamilies: Array<{ entry: FurnitureCatalogEntry; search: string }> = [];
{
  const seen = new Set<string>();
  for (const e of paletteEntries) {
    const family = furnitureFamilyId(e.id);
    if (seen.has(family)) continue;
    seen.add(family);
    const views = orientations(e.id);
    paletteFamilies.push({
      entry: views[0]?.entry ?? e,
      search: views.map((v) => v.entry.label).join(' ').toLowerCase(),
    });
  }
}
const sameFamily = (a: string | null, b: string | null): boolean =>
  !!a && !!b && furnitureFamilyId(a) === furnitureFamilyId(b);
// Deux palettes séparées : nos objets (monde du campus de Charles `ch_*`,
// objets dessinés pour Placio `pl_*`) et ceux des packs LimeZu (`lz_*`).
type Source = 'ours' | 'limezu';
const sourceOf = (e: FurnitureCatalogEntry): Source => (e.id.startsWith('lz_') ? 'limezu' : 'ours');
const SOURCES: Array<{ id: Source; label: string }> = [{ id: 'ours', label: 'Les nôtres' }, { id: 'limezu', label: 'LimeZu' }];

interface CellRect { x: number; y: number; w: number; h: number }

type Tab = 'objects' | 'sheets' | 'animated';

const LAYERS = [
  { id: 'floor', label: 'Au sol', hint: "Posé à plat sur le sol, toujours sous les avatars : on marche dessus. Pour les tapis, paillassons, marquages au sol." },
  { id: 'furniture', label: 'Normal', hint: "L'avatar passe devant l'objet quand il est plus bas que lui sur la carte, et derrière quand il est plus haut. Le bon choix pour la plupart des meubles : bureaux, chaises, plantes." },
  { id: 'furnitureTop', label: 'Devant', hint: "Toujours affiché par-dessus les avatars, qui passent dessous. Pour ce qui est en hauteur : lampes suspendues, branches d'arbres, haut d'une arche." },
];

const SOLID_HINT = "Les avatars ne peuvent pas traverser l'objet, ils doivent en faire le tour. À désactiver pour ce sur quoi on marche (tapis, ponts) ou ce qui passe au-dessus de la tête.";

const SOLID_PARTS = [
  { id: 'all', label: "Tout l'objet", hint: "Toutes les cases de l'objet bloquent. Pour ce qu'on voit de dessus : tables, bureaux, comptoirs." },
  { id: 'base', label: 'La base', hint: "Seule la rangée du bas bloque : l'avatar peut se tenir derrière la partie haute et passe alors derrière l'objet. Pour ce qui est vu de face : portes, armoires, bibliothèques, arbres. À utiliser avec le calque Normal." },
];

export function FurniturePanel({ selectedId, onSelect, layer, onLayerChange, isSolid, onSolidChange, solidBaseOnly, onSolidBaseOnlyChange }: {
  selectedId: string | null;
  onSelect: (entry: FurnitureCatalogEntry | null) => void;
  layer: string;
  onLayerChange: (layer: string) => void;
  isSolid: boolean;
  onSolidChange: (solid: boolean) => void;
  solidBaseOnly: boolean;
  onSolidBaseOnlyChange: (baseOnly: boolean) => void;
}) {
  const [tab, setTab] = useState<Tab>('objects');
  const [width, setWidth] = useState(loadPanelWidth);
  const selected = selectedId ? findFurniture(selectedId) : undefined;

  // Poignée sur le bord gauche : le panneau est à droite, tirer vers la
  // gauche l'élargit. Largeur mémorisée dans ce navigateur.
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const move = (ev: PointerEvent) => setWidth(clampWidth(startWidth + startX - ev.clientX));
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      savePanelWidth(clampWidth(startWidth + startX - ev.clientX));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <aside className="pl-epanel" aria-label="Mobilier" style={{ width }}>
      <div
        onPointerDown={startResize}
        title="Glisser pour redimensionner"
        className="pl-resize-handle"
      />

      <div className="pl-epanel-head">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 className="pl-epanel-title">Mobilier</h2>
          <p className="pl-help">
            {selected
              ? <><strong style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{selected.label}</strong> · {Math.round(selected.dimensions.w / T)}×{Math.round(selected.dimensions.h / T)} cases. Clic : poser · clic droit : retirer.</>
              : 'Choisis un objet puis clique sur la carte. Clic droit pour retirer.'}
          </p>
        </div>
        <div><Tabs aria-label="Mobilier" items={[{ id: 'objects', label: 'Objets' }, { id: 'sheets', label: 'Planches' }, { id: 'animated', label: 'Animés' }]} value={tab} onChange={setTab} /></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p className="pl-elabel">Calque</p>
          <div role="radiogroup" aria-label="Calque" className="pl-tabs" style={{ display: 'flex' }}>
            {LAYERS.map((l) => (
              <div key={l.id} className="pl-tab-with-tip">
                <InfoTip label={l.label}>{l.hint}</InfoTip>
                <button type="button" role="radio" aria-checked={layer === l.id} className={cx('pl-tab', layer === l.id && 'pl-tab-on')} onClick={() => onLayerChange(l.id)}>
                  {l.label}
                </button>
              </div>
            ))}
          </div>
        </div>
        {selected?.standing ? (
          // Objet debout : réglé d'office (standingPlacement, @campus/core).
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--color-text-secondary)' }}>
            Objet debout : seule sa base bloque, l'avatar passe derrière le haut. Réglé automatiquement.
          </p>
        ) : (<>
        <div className="pl-switch-row">
          <span className="pl-switch-text" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <InfoTip label="Bloque le passage">{SOLID_HINT}</InfoTip>
            <span>Bloque le passage</span>
          </span>
          <Switch aria-label="Bloque le passage" checked={isSolid} onChange={onSolidChange} />
        </div>
        {isSolid && (
          <div role="radiogroup" aria-label="Partie qui bloque" className="pl-tabs" style={{ display: 'flex', marginTop: -6 }}>
            {SOLID_PARTS.map((s) => {
              const on = (s.id === 'base') === solidBaseOnly;
              return (
                <div key={s.id} className="pl-tab-with-tip">
                  <InfoTip label={s.label}>{s.hint}</InfoTip>
                  <button type="button" role="radio" aria-checked={on} className={cx('pl-tab', on && 'pl-tab-on')} onClick={() => onSolidBaseOnlyChange(s.id === 'base')}>
                    {s.label}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        </>)}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tab === 'sheets'
          ? <SheetsTab selectedId={selectedId} onSelect={onSelect} />
          : <ObjectsTab key={tab} animated={tab === 'animated'} selectedId={selectedId} onSelect={onSelect} width={width} />}
      </div>
    </aside>
  );
}

const WIDTH_KEY = 'editor.furniturePanelWidth';
const MIN_WIDTH = 320;

function clampWidth(w: number): number {
  return Math.round(Math.min(Math.max(w, MIN_WIDTH), window.innerWidth * 0.7));
}

function loadPanelWidth(): number {
  try {
    const saved = Number(localStorage.getItem(WIDTH_KEY));
    if (saved) return clampWidth(saved);
  } catch { /* stockage indisponible (navigation privée…) */ }
  return DEFAULT_PANEL_WIDTH;
}

function savePanelWidth(w: number): void {
  try { localStorage.setItem(WIDTH_KEY, String(w)); } catch { /* idem */ }
}

// ── Onglet Objets ─────────────────────────────────────────────

/** Taille d'une case dans la liste d'objets (px écran). */
const LIST_CELL = 24;
/** Taille d'une case dans le détail de l'objet sélectionné. */
const DETAIL_CELL = 48;
/** Hauteur max d'une vignette dans la liste, et du détail (px écran). */
const LIST_MAX_HEIGHT = 160;
const DETAIL_MAX_HEIGHT = 420;

function ObjectsTab({ animated, selectedId, onSelect, width }: {
  /** true : onglet Animés (objets `anim`), false : onglet Objets (tout le reste). */
  animated: boolean;
  selectedId: string | null;
  onSelect: (entry: FurnitureCatalogEntry | null) => void;
  width: number;
}) {
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<Source>('ours');
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  // Objet affiché en détail : reste le même quand on n'en sélectionne qu'une partie.
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // La vue choisie (palette ou touche R) remplace celle de l'objet en détail.
  const focused = focusedId ? findFurniture(sameFamily(selectedId, focusedId) ? selectedId! : focusedId) : undefined;

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups: Array<{ id: string; label: string; items: FurnitureCatalogEntry[] }> = [];
    for (const category of FURNITURE_CATEGORIES) {
      const items = paletteFamilies.filter(
        ({ entry: e, search: text }) => e.category === category.id && !!e.anim === animated
          && (animated || sourceOf(e) === source)
          && (!q || text.includes(q)),
      ).map((f) => f.entry);
      if (items.length > 0) groups.push({ ...category, items });
    }
    return groups;
  }, [search, animated, source]);

  const toggleCategory = (cat: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const pickObject = (entry: FurnitureCatalogEntry) => {
    if (sameFamily(selectedId, entry.id)) {
      setFocusedId(null);
      onSelect(null);
      return;
    }
    setFocusedId(entry.id);
    onSelect(entry);
  };

  // Partie de l'objet → tampon dans sa planche. L'image affichée est déjà
  // retournée pour un objet `_fx` : la colonne affichée x correspond à la
  // colonne source (largeur - 1 - x).
  const pickPart = (entry: FurnitureCatalogEntry, r: CellRect) => {
    const s = entry.stamp!;
    if (r.x === 0 && r.y === 0 && r.w === s.tw && r.h === s.th) {
      onSelect(entry);
      return;
    }
    const tx = s.flipX ? s.tx + s.tw - r.x - r.w : s.tx + r.x;
    const part = findFurniture(stampId({ sheet: s.sheet, tx, ty: s.ty + r.y, tw: r.w, th: r.h, flipX: s.flipX }));
    if (part) onSelect(part);
  };

  const partRect = (entry: FurnitureCatalogEntry): CellRect | null => {
    const s = entry.stamp;
    const sel = selectedId ? findFurniture(selectedId)?.stamp : undefined;
    if (!s || !sel || sel.sheet !== s.sheet || sel.flipX !== s.flipX) return null;
    const x = s.flipX ? s.tx + s.tw - sel.tx - sel.tw : sel.tx - s.tx;
    const y = sel.ty - s.ty;
    if (x < 0 || y < 0 || x + sel.tw > s.tw || y + sel.th > s.th) return null;
    return { x, y, w: sel.tw, h: sel.th };
  };

  return (
    <>
      <div style={{ padding: '12px 16px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!animated && <div><Tabs aria-label="Origine du mobilier" items={SOURCES} value={source} onChange={setSource} /></div>}
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={animated ? 'Rechercher un objet animé' : 'Rechercher un objet'} aria-label="Rechercher un objet" />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 16px' }}>
        {grouped.map(({ id: category, label, items }) => {
          // Une recherche active déplie tous les groupes qui ont un résultat.
          const isOpen = search.trim() !== '' || openCategories.has(category);
          return (
            <div key={category}>
              <button type="button" className="pl-cat" aria-expanded={isOpen} onClick={() => toggleCategory(category)}>
                <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={14} />
                {label}
                <span className="pl-count">{items.length}</span>
              </button>

              {isOpen && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 6, padding: '4px 12px 10px' }}>
                  {items.map((family) => {
                    const active = sameFamily(focusedId, family.id) || sameFamily(selectedId, family.id);
                    // Vignette d'un objet à vues : la vue choisie s'il est sélectionné.
                    const entry = (sameFamily(selectedId, family.id) && findFurniture(selectedId!)) || family;
                    const views = orientations(family.id).length;
                    return (
                      <button
                        key={family.id}
                        type="button"
                        aria-pressed={active}
                        aria-label={entry.label}
                        onClick={() => pickObject(family)}
                        title={`${entry.label} (${Math.round(entry.dimensions.w / T)}×${Math.round(entry.dimensions.h / T)})${views > 1 ? ` · ${views} vues` : ''}`}
                        className={cx('pl-fthumb', active && 'pl-fthumb-on')}
                        style={views > 1 ? { position: 'relative' } : undefined}
                      >
                        <GridImage entry={entry} cell={LIST_CELL} maxWidth={width - 44} />
                        {views > 1 && <span className="pl-fthumb-views" aria-hidden="true">{views}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {focused && (
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: '10px 16px', maxHeight: '40%', overflow: 'auto' }}>
          {orientations(focused.id).length > 1 && (
            <div style={{ marginBottom: 8 }}>
              <ViewPicker
                id={focused.id}
                onPick={(id) => {
                  const entry = findFurniture(id);
                  if (!entry) return;
                  setFocusedId(id);
                  onSelect(entry);
                }}
              />
            </div>
          )}
          <p className="pl-help" style={{ marginBottom: 8 }}>
            {focused.stamp
              ? 'Glisse sur les cases pour ne poser qu\u2019une partie de l\u2019objet.'
              : 'Cet objet se pose en entier.'}
          </p>
          {focused.stamp ? (
            <CellPicker
              cols={focused.stamp.tw}
              rows={focused.stamp.th}
              cell={Math.min(DETAIL_CELL, Math.floor((width - 44) / focused.stamp.tw), Math.floor(DETAIL_MAX_HEIGHT / focused.stamp.th))}
              layers={[{ url: focused.url, top: 0 }]}
              selection={partRect(focused)}
              onSelect={(r) => pickPart(focused, r)}
            />
          ) : (
            <div style={{ display: 'inline-block', background: PREVIEW_BG, lineHeight: 0 }}>
              <GridImage entry={focused} cell={DETAIL_CELL} maxWidth={width - 44} maxHeight={DETAIL_MAX_HEIGHT} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Choix de la vue d'un objet décliné en vues (face, droite, dos, gauche) :
 * détail de la palette, et barre du meuble posé sélectionné sur la carte.
 */
export function ViewPicker({ id, onPick }: { id: string; onPick: (id: string) => void }) {
  const views = orientations(id);
  return (
    <div className="pl-views">
      <span className="pl-elabel">Vue</span>
      <div role="radiogroup" aria-label="Vue de l'objet" className="pl-tabs" style={{ display: 'flex' }}>
        {views.map(({ view, entry }) => (
          <button
            key={entry.id}
            type="button"
            role="radio"
            aria-checked={entry.id === id}
            className={cx('pl-tab', entry.id === id && 'pl-tab-on')}
            onClick={() => { if (entry.id !== id) onPick(entry.id); }}
            title={entry.label}
          >
            {view ? FURNITURE_VIEW_LABELS[view] : entry.label}
          </button>
        ))}
      </div>
      <span className="pl-help" title="Maj+R : vue précédente">R</span>
    </div>
  );
}

/** Image d'un objet à l'échelle `cell` px par case, grille superposée (aussi : DeskCustomizePanel). */
export function GridImage({ entry, cell, maxWidth, maxHeight = LIST_MAX_HEIGHT }: {
  entry: FurnitureCatalogEntry; cell: number; maxWidth: number; maxHeight?: number;
}) {
  const cols = Math.max(1, Math.round(entry.dimensions.w / T));
  const rows = Math.max(1, Math.round(entry.dimensions.h / T));
  // Grands ensembles (palais : 25×34 cases) : réduits pour tenir dans la liste.
  const c = Math.min(cell, maxWidth / cols, maxHeight / rows);
  // Taille réelle × échelle (un single LimeZu n'est pas toujours un multiple de 32 px).
  const w = (entry.dimensions.w * c) / T, h = (entry.dimensions.h * c) / T;
  return (
    <div style={{ position: 'relative', width: w, height: h }}>
      {entry.anim ? (
        <AnimatedStrip url={entry.url} frames={entry.anim.frames} frameRate={entry.anim.frameRate} width={w} height={h} />
      ) : (
        <img
          src={entry.url}
          alt={entry.label}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', imageRendering: 'pixelated' }}
        />
      )}
      <div style={{ position: 'absolute', inset: 0, ...gridBackground(c) }} />
    </div>
  );
}

/**
 * Objet animé : sa bande d'images jouée en boucle, comme en jeu. Animation
 * CSS (steps) plutôt qu'un minuteur par vignette — la palette peut en
 * afficher des dizaines à la fois.
 */
const STRIP_KEYFRAMES = '@keyframes furniture-strip { to { background-position-x: calc(-1 * var(--strip-w)); } }';

function AnimatedStrip({ url, frames, frameRate, width, height }: {
  url: string; frames: number; frameRate: number; width: number; height: number;
}) {
  return (
    <>
      <style>{STRIP_KEYFRAMES}</style>
      <div
        role="img"
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${url})`,
          backgroundSize: `${width * frames}px ${height}px`,
          backgroundPositionX: 0,
          imageRendering: 'pixelated',
          ['--strip-w' as string]: `${width * frames}px`,
          animation: `furniture-strip ${frames / frameRate}s steps(${frames}) infinite`,
        }}
      />
    </>
  );
}

// ── Onglet Planches ───────────────────────────────────────────

const ZOOMS = [1, 1.5, 2];

// Liste de l'onglet : planches « listées » seulement (les ensembles LimeZu se
// posent depuis l'onglet Objets), les nôtres (`placio_*`) à part, puis LimeZu
// en Intérieurs / Extérieurs.
const listedSheets = sheetCatalog.filter((s) => s.listed !== false);
const ownSheet = (slug: string) => slug.startsWith('placio_');
const SHEET_GROUPS = [
  { label: 'Les nôtres', sheets: listedSheets.filter((s) => ownSheet(s.slug)) },
  { label: 'LimeZu, intérieurs', sheets: listedSheets.filter((s) => !ownSheet(s.slug) && !s.slug.startsWith('ext_')) },
  { label: 'LimeZu, extérieurs', sheets: listedSheets.filter((s) => s.slug.startsWith('ext_')) },
];

function SheetsTab({ selectedId, onSelect }: {
  selectedId: string | null;
  onSelect: (entry: FurnitureCatalogEntry | null) => void;
}) {
  const [sheetSlug, setSheetSlug] = useState(listedSheets[0]?.slug ?? '');
  const [zoom, setZoom] = useState(1);
  const [flipX, setFlipX] = useState(false);
  const sheet = findSheet(sheetSlug);

  if (!sheet) return <p className="pl-help" style={{ padding: 16 }}>Aucune planche publiée.</p>;

  const current = selectedId ? findFurniture(selectedId)?.stamp : undefined;
  const selection: CellRect | null = current && current.sheet === sheet.slug
    ? { x: current.tx, y: current.ty, w: current.tw, h: current.th }
    : null;

  const select = (r: CellRect, flip = flipX) => {
    const stamp: SheetStamp = { sheet: sheet.slug, tx: r.x, ty: r.y, tw: r.w, th: r.h, flipX: flip };
    const entry = findFurniture(stampId(stamp));
    if (entry) onSelect(entry);
  };

  const toggleFlip = () => {
    const next = !flipX;
    setFlipX(next);
    if (selection) select(selection, next);
  };

  // La planche est affichée à son échelle native × zoom (32 px par case à 1×).
  const cell = T * zoom;

  return (
    <>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Select natif stylé comme le design system : ds/Select ne gère pas les groupes d'options. */}
        <div className="pl-select-wrap pl-select-sm">
          <select className="pl-select-input" aria-label="Planche" value={sheet.slug} onChange={(e) => setSheetSlug(e.target.value)}>
            {SHEET_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.sheets.map((s) => <option key={s.slug} value={s.slug}>{s.label.replace(/^Ext\. — /, '')}</option>)}
              </optgroup>
            ))}
          </select>
          <Icon name="chevron-down" size={14} className="pl-select-chevron" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Tabs aria-label="Zoom de la planche" items={ZOOMS.map((z) => ({ id: String(z), label: `${z}×` }))} value={String(zoom)} onChange={(z) => setZoom(Number(z))} />
          <Button variant={flipX ? 'primary' : 'secondary'} size="sm" aria-pressed={flipX} onClick={toggleFlip} title="Poser la sélection retournée (miroir horizontal), comme le retournement de tuile de Tiled">
            Miroir
          </Button>
        </div>
        <p className="pl-help">Glisse sur la planche pour sélectionner un groupe de cases.</p>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
        <CellPicker
          key={sheet.slug}
          cols={sheet.cols}
          rows={sheet.rows}
          cell={cell}
          layers={sheet.pages.map((url, p) => ({ url, top: p * sheet.pageRows }))}
          selection={selection}
          onSelect={(r) => select(r)}
        />
      </div>
    </>
  );
}

// ── Sélection de cases (commun aux deux onglets) ─────────────

/**
 * Images (pages d'une planche ou image d'un objet) sur une grille de
 * `cols`×`rows` cases ; glisser sélectionne un rectangle de cases.
 */
function CellPicker({ cols, rows, cell, layers, selection, onSelect }: {
  cols: number;
  rows: number;
  cell: number;
  /** `top` en cases. */
  layers: Array<{ url: string; top: number }>;
  selection: CellRect | null;
  onSelect: (r: CellRect) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  const cellAt = (e: React.PointerEvent) => {
    const box = ref.current!.getBoundingClientRect();
    return {
      x: Math.min(cols - 1, Math.max(0, Math.floor((e.clientX - box.left) / cell))),
      y: Math.min(rows - 1, Math.max(0, Math.floor((e.clientY - box.top) / cell))),
    };
  };
  const toRect = (d: { x0: number; y0: number; x1: number; y1: number }): CellRect => ({
    x: Math.min(d.x0, d.x1), y: Math.min(d.y0, d.y1),
    w: Math.abs(d.x1 - d.x0) + 1, h: Math.abs(d.y1 - d.y0) + 1,
  });

  const shown = drag ? toRect(drag) : selection;

  return (
    <div
      ref={ref}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const c = cellAt(e);
        setDrag({ x0: c.x, y0: c.y, x1: c.x, y1: c.y });
      }}
      onPointerMove={(e) => {
        if (!drag) return;
        const c = cellAt(e);
        if (c.x !== drag.x1 || c.y !== drag.y1) setDrag({ ...drag, x1: c.x, y1: c.y });
      }}
      onPointerUp={() => {
        if (!drag) return;
        onSelect(toRect(drag));
        setDrag(null);
      }}
      style={{
        position: 'relative', width: cols * cell, height: rows * cell,
        background: PREVIEW_BG, cursor: 'crosshair', userSelect: 'none', touchAction: 'none',
      }}
    >
      {layers.map((l) => (
        <img
          key={l.url}
          src={l.url}
          alt=""
          draggable={false}
          style={{
            position: 'absolute', left: 0, top: l.top * cell,
            width: cols * cell, imageRendering: 'pixelated', pointerEvents: 'none',
          }}
        />
      ))}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', ...gridBackground(cell) }} />
      {shown && (
        <div style={{
          position: 'absolute', pointerEvents: 'none',
          left: shown.x * cell, top: shown.y * cell, width: shown.w * cell, height: shown.h * cell,
          border: '2px solid var(--color-focus)', background: 'var(--color-primary-muted)',
          boxSizing: 'border-box',
        }} />
      )}
    </div>
  );
}

function gridBackground(cell: number): React.CSSProperties {
  return {
    backgroundImage: `linear-gradient(to right, ${GRID_LINE} 1px, transparent 1px), linear-gradient(to bottom, ${GRID_LINE} 1px, transparent 1px)`,
    backgroundSize: `${cell}px ${cell}px`,
  };
}
